import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';
import type { Pillar } from '@/types/pillars';

import { PillarStatus } from '@/constants/PillarStatus';
import { validateIconBackground, validateIconId } from '@/utils/companyValueValidation';
import { validateUniqueLocalizedName } from '@/utils/localizedNameValidation';

export const NAME_MAX_LENGTH = 100;
export const DESCRIPTION_MAX_LENGTH = 300;

const NAME_REQUIRED_MESSAGE: TranslationKey = 'pillars.nameRequired';
const NAME_MAX_LENGTH_MESSAGE: TranslationKey = 'pillars.nameMaxLength';
const NAME_DUPLICATE_MESSAGE: TranslationKey = 'pillars.nameDuplicateError';
const DESCRIPTION_MAX_LENGTH_MESSAGE: TranslationKey = 'pillars.descriptionMaxLength';
const PERIOD_START_REQUIRED_MESSAGE: TranslationKey = 'pillars.periodStartRequired';
const PERIOD_END_REQUIRED_MESSAGE: TranslationKey = 'pillars.periodEndRequired';
const PERIOD_END_BEFORE_START_MESSAGE: TranslationKey = 'pillars.periodEndBeforeStart';

export type PillarFormData = {
    name: string;
    description?: string;
    icon?: string;
    iconBackground?: string;
    // Optional to match the schema's input type (the enum carries a `.default`); the component
    // always supplies a concrete value via `defaultValues`/`reset`.
    status?: Pillar['status'];
    periodStartDate?: string | null;
    periodEndDate?: string | null;
    // Optional to match the schema's input type (the boolean carries a `.default`).
    isIndefinite?: boolean;
};

export interface PillarSchemaParams {
    existingPillars: Pillar[];
    currentPillarId: string | undefined;
    currentLanguage: string;
}

/**
 * Creates the Zod schema for the AddEditPillarModal form.
 * The schema depends on runtime values (existing pillars, language) so it is returned as a factory.
 */
export const createPillarSchema = ({
    existingPillars,
    currentPillarId,
    currentLanguage,
}: PillarSchemaParams) =>
    z
        .object({
            name: z
                .string()
                .min(1, { message: NAME_REQUIRED_MESSAGE })
                .max(NAME_MAX_LENGTH, { message: NAME_MAX_LENGTH_MESSAGE })
                .superRefine(
                    validateUniqueLocalizedName({
                        existing: existingPillars,
                        currentId: currentPillarId,
                        language: currentLanguage,
                        message: NAME_DUPLICATE_MESSAGE,
                    }),
                ),
            description: z
                .string()
                .max(DESCRIPTION_MAX_LENGTH, { message: DESCRIPTION_MAX_LENGTH_MESSAGE })
                .optional(),
            icon: z.string().optional().superRefine(validateIconId),
            iconBackground: z.string().optional().superRefine(validateIconBackground),
            status: z
                .enum([PillarStatus.ACTIVE, PillarStatus.DRAFT, PillarStatus.INACTIVE])
                .default(PillarStatus.ACTIVE),
            periodStartDate: z.string().nullable().optional(),
            periodEndDate: z.string().nullable().optional(),
            isIndefinite: z.boolean().default(false),
        })
        // Both dates are optional, but an end date needs a start date, an end date can never
        // precede its start, and a start with no end is only ever "active indefinitely" -- which
        // the checkbox has to claim, since nothing else tells the two apart once it is saved.
        .superRefine(({ periodStartDate, periodEndDate, isIndefinite }, ctx) => {
            if (periodEndDate && !periodStartDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: PERIOD_START_REQUIRED_MESSAGE,
                    path: ['periodStartDate'],
                });
                return;
            }

            if (periodStartDate && !periodEndDate && !isIndefinite) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: PERIOD_END_REQUIRED_MESSAGE,
                    path: ['periodEndDate'],
                });
                return;
            }

            if (periodStartDate && periodEndDate && periodEndDate < periodStartDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: PERIOD_END_BEFORE_START_MESSAGE,
                    path: ['periodEndDate'],
                });
            }
        });
